import { FC, useMemo } from "react";
import { AssortmentTaskType, TaskAssortmentMatrix } from "tm-shared/types";

import { Loading } from "@/components/Loading";
import { StepActionButtonUi } from "@/components/StepActionButtonUi";
import { TASK_SCREEN_RECORDING_USER_IDS } from "@/config";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useCompleteTask } from "@/hooks/useCompleteTask";
import { makeCommand, useMakeCommand } from "@/hooks/useRequestQueue";
import { useTaskConfigSelector } from "@/hooks/useTaskSelectors";
import { openReplayService } from "@/Services/OpenReplay";
import { setMdTasksConfig } from "@/store/slices/ui";

import { getNextSubtaskId } from "../AssortmentMatrix.utils";

interface StepButtonProps {
    task: TaskAssortmentMatrix;
    marketId: string;
    currentCompleted: boolean;
}

export const StepButton: FC<StepButtonProps> = ({
    task,
    marketId,
    currentCompleted = false,
}) => {
    const { id: userId } = useAppSelector((s) => s.user);

    const dispatch = useAppDispatch();

    const taskId = task.id;
    const subtaskIds = task.context.subtasks.map((t) => t.subtaskId);

    const { activeItem: activeStepKey } = useTaskConfigSelector({
        marketId,
        taskId,
    });

    const taskPayload = useAppSelector(
        (s) => s.progress.assortmentMatrix[taskId],
    );

    const allCompleted = useMemo(() => {
        if (!taskPayload) return false;

        const subtaskPayloads = Object.values(taskPayload);

        if (subtaskPayloads.length !== subtaskIds.length) return false;

        for (const p of subtaskPayloads) {
            if (!p.completed) return false;
        }

        return true;
    }, [taskPayload, subtaskIds]);

    const { nextSubtaskId, isLast } = getNextSubtaskId({
        subtasks: task.context.subtasks,
        currentSubtaskId: activeStepKey,
    });

    const buttonDisabled = isLast && !allCompleted && currentCompleted;

    const onNext = () => {
        dispatch(
            setMdTasksConfig({
                marketId,
                taskId,
                field: "activeItem",
                value: nextSubtaskId,
            }),
        );
    };

    const { makeTaskCompleteCommands, makeUploadPhotoCommands } =
        useMakeCommand();

    const completeTask = useCompleteTask({ marketId, taskId });

    const [handleComplete, completeLoading] = useAsyncAction(async () => {
        const photoIds = Object.values(taskPayload).reduce((result, item) => {
            if (item.type === AssortmentTaskType.FinGroupPhotos) {
                const currentIds = Object.values(item.tasks)
                    .map((task) => task?.photos)
                    .flat();

                return [...result, ...currentIds];
            }

            return result;
        }, []);

        const hasPhoto = photoIds.length > 0;

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: { taskId, hasPhoto, payload: taskPayload },
            marketId,
        });

        const commands = [
            ...makeUploadPhotoCommands(photoIds),

            ...taskCompleteCommands,

            makeCommand({
                type: "updatePhotoStorage",
                title: "Синхронізація сховища",
                params: photoIds,
                options: {
                    stopOnError: false,
                },
            }),
        ];

        completeTask({ commands });

        if (TASK_SCREEN_RECORDING_USER_IDS.includes(userId)) {
            openReplayService.stopTaskRecording(taskId, taskPayload);
        }
    });

    return (
        <>
            {currentCompleted && (
                <StepActionButtonUi
                    onNext={onNext}
                    onFinish={handleComplete}
                    isLast={isLast}
                    disabled={buttonDisabled}
                    loading={completeLoading}
                />
            )}

            {completeLoading && <Loading title="Завершення задачі" />}
        </>
    );
};
